/**
 * webhid-controller.js — Web HID lifecycle helper for game controllers.
 *
 * Wraps `navigator.hid.requestDevice()` + `device.open()` + input-report
 * subscription with retries, vendor-id resolution, and graceful fallback when
 * Web HID is unavailable (Safari, Firefox, console browsers, iOS, http: origin).
 *
 * Vendor IDs (USB-IF assigned):
 *   Sony       0x054c   — DualShock 4, DualSense, DualSense Edge
 *   Microsoft  0x045e   — Xbox One / Series / Elite controllers
 *   Nintendo   0x057e   — Joy-Con (L/R), Pro Controller
 *   Valve      0x28de   — Steam Controller, Steam Deck (forwarded)
 *
 * Public API (mounted as window.WebHIDController):
 *   .available()     → boolean — Web HID present + secure context
 *   .request(filters)→ Promise<HIDDevice|null>
 *                      filters defaults: ALL known controller vendors.
 *                      Returns null on user-cancel; never rejects on cancel.
 *   .open(device)    → Promise<HIDDevice>
 *   .close(device)   → Promise<void>
 *   .vendor(device)  → 'sony' | 'microsoft' | 'nintendo' | 'valve' | 'unknown'
 *   .listen(device, handler) → unsubscribe()
 *      handler({reportId, data: DataView, device})
 *
 * Caller-side concerns NOT in this module (intentionally):
 *   - parsing input reports (controller-specific — Joy-Con vs DualSense have
 *     different report layouts; that's the consumer tool's job)
 *   - sending feature/output reports (consumer tool calls device.sendFeatureReport
 *     directly; we expose `device` so the consumer owns proprietary protocol)
 *
 * Why this is a separate module: the connection lifecycle (request → open →
 * forgetting to close → page-unload) is identical for DualSense / Joy-Con /
 * Xbox testers; the input report parsing is not. Sharing the lifecycle eliminates
 * 3 copies of cleanup code.
 */
(function(){
    'use strict';

    if (window.WebHIDController && !window.WebHIDController.__isStub) return;

    var VENDORS = {
        0x054c: 'sony',
        0x045e: 'microsoft',
        0x057e: 'nintendo',
        0x28de: 'valve'
    };

    var DEFAULT_FILTERS = [
        { vendorId: 0x054c }, // Sony
        { vendorId: 0x045e }, // Microsoft
        { vendorId: 0x057e }, // Nintendo
        { vendorId: 0x28de }  // Valve
    ];

    function _available() {
        // Web HID requires secure context AND a privileged-feature flag in HTTPS.
        // navigator.hid is the singleton; absence = no support.
        return ('hid' in navigator) && !!navigator.hid;
    }

    function _vendor(device) {
        if (!device || typeof device.vendorId !== 'number') return 'unknown';
        return VENDORS[device.vendorId] || 'unknown';
    }

    function _request(filters) {
        if (!_available()) return Promise.resolve(null);
        var f = (filters && filters.length) ? filters : DEFAULT_FILTERS;
        return navigator.hid.requestDevice({ filters: f }).then(function(arr) {
            // requestDevice returns array — user-pick first or null on cancel.
            return (arr && arr.length) ? arr[0] : null;
        }).catch(function() {
            // User cancel raises NotFoundError on some browsers — treat as null.
            return null;
        });
    }

    function _open(device) {
        if (!device) return Promise.reject(new Error('No device'));
        if (device.opened) return Promise.resolve(device);
        return device.open().then(function() { return device; });
    }

    function _close(device) {
        if (!device || !device.opened) return Promise.resolve();
        try { return device.close(); } catch (_) { return Promise.resolve(); }
    }

    function _listen(device, handler) {
        if (!device) return function(){};
        function on(e) {
            try {
                handler({
                    reportId: e.reportId,
                    data:     e.data,
                    device:   e.device || device
                });
            } catch (err) {
                if (window.__handleError) window.__handleError(err, 'webhid-listen');
            }
        }
        device.addEventListener('inputreport', on);
        return function unsubscribe() {
            try { device.removeEventListener('inputreport', on); } catch (_) {}
        };
    }

    var WebHIDController = {
        available: _available,
        vendor:    _vendor,
        request:   _request,
        open:      _open,
        close:     _close,
        listen:    _listen
    };

    Object.defineProperty(WebHIDController, '__isStub', { value: false });
    window.WebHIDController = WebHIDController;
})();
